import dayjs from 'dayjs';
import {
  CANCEL_REQUESTED,
  CANCELLED,
  COMPLETED,
  IN_PROGRESS,
  SHIPPED,
} from '../constants/lineItemState';
import type {
  ILineItemState,
  ItemStatusDisplay,
} from '../types/OrderHistory.types';
import { itemStatusMap } from '../constants/itemStatusMap';

const statusColorMap: Record<string, string> = {
  [IN_PROGRESS]: 'success',
  [SHIPPED]: 'success',
  [COMPLETED]: 'success',
  [CANCELLED]: 'error',
  [CANCEL_REQUESTED]: 'pending',
};

/**
 * @param {array} states containing state objects in the shape of: { quantity: int, state: string }
 * @param {boolean} isDeliverable - whether the item is deliverable or not
 * Return value will always be an array, even if there is only one state.
 */
export const mapItemStatesToStatuses = (
  states: ILineItemState[],
  isDeliverable: boolean
): ItemStatusDisplay[] => {
  if (!states.length) {
    return [];
  }

  // When we have more than one state we want to show each status with quantity affected
  const includeQuantity = states.length > 1;

  return states.map(({ state, quantity, detail }) => {
    let statusTranslationKey = itemStatusMap[state] as string;
    if (!isDeliverable && statusTranslationKey === SHIPPED) {
      // Don't display 'Shipped' if non-physical item is fulfilled.
      statusTranslationKey = COMPLETED;
    }

    const color =
      statusColorMap[statusTranslationKey as keyof typeof statusColorMap] ??
      'standard';

    return {
      statusTranslationKey,
      detail,
      quantity: includeQuantity ? quantity : undefined,
      color,
    };
  });
};

/**
 * Determines whether an item in this set of states should display its promised arrival date
 * If any status in the status list argument has the value IN_PROGRESS or SHIPPED, we should display a CMRD.
 * @param {string} promisedArrivalDate - promised arrival date of the item
 * @param {array} [statuses] - array of status strings (i.e., the output of `mapItemStatesToStatuses`).
 * @param {boolean} [hidePastPromisedArrivalDates] - hides promised arrival dates that are in the past
 * @param {boolean} [isFulfillmentHistoryEnabledForLineItem] - whether the fulfillment history feature is enabled
 */
export const shouldShowPromisedArrivalDate = (
  promisedArrivalDate: string,
  itemStatuses: ItemStatusDisplay[],
  hidePastPromisedArrivalDates: boolean,
  isFulfillmentHistoryEnabledForLineItem: boolean
) => {
  if (isFulfillmentHistoryEnabledForLineItem) {
    return false;
  }
  const today = dayjs();
  const arrivalDate = dayjs(promisedArrivalDate);

  if (hidePastPromisedArrivalDates && arrivalDate.isBefore(today)) {
    return false;
  }

  return (
    itemStatuses.filter(
      (itemStatus) =>
        itemStatus.statusTranslationKey === IN_PROGRESS ||
        itemStatus.statusTranslationKey === SHIPPED
    ).length > 0
  );
};
